"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { api, ApiError } from "./api";

export type StaffRole = {
  id: string;
  roleCode: string;
  scopeMode: "EXPLICIT_SET" | "SUBTREE_DYNAMIC";
  scopeOrganizationIds: string[];
  validTo: string | null;
  reviewStatus: "PENDING_REVIEW" | "REVIEWED" | "NOT_REQUIRED";
  grantBasis: "STAFF_ASSIGNMENT" | "APPOINTMENT" | "DELEGATION";
};

/**
 * 生效中的授权。
 *
 * `whoami` 把待复核的也一起给出来——本人有权知道自己被授了什么、卡在哪。
 * 但界面判「能不能进这个台」只能看生效的那些：服务端的判定已经把
 * PENDING_REVIEW 当作不生效（ADR-0030 §3.2），前端若照单全收，
 * 就会给人一个点进去必然 403 的入口。两处必须同一口径。
 */
export function effectiveRoles(whoami: WhoAmI | null): StaffRole[] {
  return (whoami?.roles ?? []).filter(
    (role) => role.reviewStatus !== "PENDING_REVIEW"
  );
}

export type WhoAmI = {
  accountId: string;
  displayName: string;
  domainId: string;
  roles: StaffRole[];
};

type SessionState = {
  phase: "LOADING" | "ANONYMOUS" | "READY" | "ERROR";
  whoami: WhoAmI | null;
  error: string | null;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  hasRole(roleCode: string): boolean;
};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<SessionState["phase"]>("LOADING");
  const [whoami, setWhoami] = useState<WhoAmI | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const result = await api<WhoAmI>("/api/v1/auth/whoami");
      // 200 不等于「这是一个会话」。api() 只看 response.ok，泛型是断言不是校验——
      // 反代若被指到别的服务上（比如默认 3000 端口上恰好是另一个项目），
      // 它可能回 200 + 一个完全不同的 JSON，于是这里拿到一个没名字、没职责的
      // 「会话」，整个台就渲染成「登进来了但什么权都没有」——比报错更难查。
      // 会话必须有 accountId；没有就是没读到身份，照实说。
      if (!result || typeof result.accountId !== "string" || !Array.isArray(result.roles)) {
        setWhoami(null);
        setError("身份接口回了预期之外的内容，请检查 API_INTERNAL_BASE_URL 指向");
        setPhase("ERROR");
        return;
      }
      setWhoami(result);
      setPhase("READY");
    } catch (caught: unknown) {
      if (caught instanceof ApiError && caught.status === 401) {
        setWhoami(null);
        setPhase("ANONYMOUS");
        return;
      }
      setError("暂时读不到当前身份");
      setPhase("ERROR");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      await api("/api/v1/auth/login", {
        method: "POST",
        body: { username, password }
      });
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    await api("/api/v1/auth/logout", { method: "POST" }).catch(() => undefined);
    setWhoami(null);
    setPhase("ANONYMOUS");
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      phase,
      whoami,
      error,
      login,
      logout,
      refresh,
      hasRole: (roleCode) =>
        effectiveRoles(whoami).some((role) => role.roleCode === roleCode)
    }),
    [phase, whoami, error, login, logout, refresh]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}

// 授权范围里可用的组织。起草时要选承办组织，选项只能来自自己的授权范围，
// 不能给一个下拉框把全域组织都列出来。
export function scopeOrganizationIds(whoami: WhoAmI | null): string[] {
  return [
    ...new Set(
      effectiveRoles(whoami).flatMap((role) => role.scopeOrganizationIds)
    )
  ];
}
